import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import {
  fetchMyDemandDetail,
  closeDemand,
  submitDemandAudit,
  fetchEnterpriseDemandBids,
  fetchEnterpriseDemandBidTimeline,
  awardDemandBid,
} from '@/service/api/demand';
import { fetchGetPublicProviderDetail, fetchViewProviderContact } from '@/service/api/provider';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { formatCurrencyPrice } from '@/utils/currency';
import Toast from '@/components/common/Toast';

/** 使用 currency.ts 工具函数格式化需求预算 */
function formatDemandBudget(demand?: Api.Demand.DemandResponse | null): string {
  if (!demand) return '面议';
  const { budget_display, budget_type } = demand;
  if (!budget_display || budget_type === 'NEGOTIABLE' || budget_display.includes('面议')) {
    return '面议';
  }

  const currencyMatch = budget_display.match(/([A-Z]{3})/i);
  const currency = currencyMatch ? currencyMatch[1] : 'CNY';

  const rangeMatch = budget_display.match(/^(\d+(\.\d+)?)\s*-\s*(\d+(\.\d+)?)/);
  if (rangeMatch) {
    const minVal = rangeMatch[1];
    const maxVal = rangeMatch[3];
    return `${formatCurrencyPrice(minVal, currency)} ~ ${formatCurrencyPrice(maxVal, currency)}`;
  }

  const singleMatch = budget_display.match(/^(\d+(\.\d+)?)/);
  if (singleMatch) {
    return formatCurrencyPrice(singleMatch[1], currency);
  }

  return budget_display.replace(/\.00/g, '');
}

/** 格式化日期为 YYYY-MM-DD */
function formatDateShort(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** 格式化完整日期时间 */
function formatDateTime(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 格式化文件大小 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 状态样式配置 */
function getStatusBadge(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING_REVIEW' || s === 'PENDING' || s === 'IN_AUDIT') {
    return { label: '审核中', bg: 'bg-amber-50 text-amber-700 border-amber-200', type: 'in_audit' };
  }
  if (s === 'REJECTED' || s === 'AUDIT_REJECTED') {
    return { label: '已驳回', bg: 'bg-red-50 text-red-600 border-red-200', type: 'rejected' };
  }
  if (s === 'RECRUITING' || s === 'PUBLISHED' || s === 'APPROVED') {
    return { label: '招募中', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', type: 'published' };
  }
  if (s === 'DRAFT') {
    return { label: '草稿', bg: 'bg-slate-100 text-slate-700 border-slate-200', type: 'draft' };
  }
  if (s === 'CLOSED') {
    return { label: '已关闭', bg: 'bg-rose-50 text-rose-600 border-rose-200', type: 'closed' };
  }
  if (s === 'EXPIRED') {
    return { label: '已过期', bg: 'bg-gray-100 text-gray-500 border-gray-200', type: 'expired' };
  }
  return { label: status || '未知状态', bg: 'bg-slate-100 text-slate-600 border-slate-200', type: 'other' };
}

/** 报价状态展示 */
function getBidStatusBadge(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'SELECTED' || s === 'AWARDED' || s === 'ACCEPTED' || s === 'SUCCESS') {
    return { label: '已中标', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  }
  if (s === 'REJECTED' || s === 'DECLINED' || s === 'FAILED') {
    return { label: '未采纳', color: 'bg-rose-50 text-rose-600 border-rose-200' };
  }
  if (s === 'WITHDRAWN' || s === 'CANCELLED') {
    return { label: '已撤回', color: 'bg-gray-100 text-gray-500 border-gray-200' };
  }
  return { label: '待响应', color: 'bg-amber-50 text-amber-700 border-amber-200' };
}

interface CountdownInfo {
  text: string;
  isExpired: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** 格式化招募截止倒计时文本 */
function formatCountdown(targetDateStr?: string): CountdownInfo {
  if (!targetDateStr) {
    return { text: '未设置截止时间', isExpired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const targetTime = new Date(targetDateStr).getTime();
  if (isNaN(targetTime)) {
    return { text: '截止时间无效', isExpired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const diff = targetTime - Date.now();

  if (diff <= 0) {
    return { text: '招募已截止', isExpired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  const pad = (n: number) => String(n).padStart(2, '0');

  let text = '';
  if (days > 0) {
    text = `${days}天 ${pad(hours)}小时 ${pad(minutes)}分 ${pad(seconds)}秒`;
  } else {
    text = `${pad(hours)}小时 ${pad(minutes)}分 ${pad(seconds)}秒`;
  }

  return { text, isExpired: false, days, hours, minutes, seconds };
}

/** 获取时间线事件详情展示配置 */
function getTimelineEventDetails(event: Api.Demand.DemandBidEventResponse) {
  const type = (event.event_type || '').toUpperCase();
  switch (type) {
    case 'DEMAND_PUBLISHED':
      return {
        title: '需求发布',
        description: '需求审核通过并成功发布',
        dotBg: 'bg-emerald-500',
        icon: 'ri-checkbox-circle-line',
      };
    case 'BID_OPENED':
      return {
        title: '竞标开启',
        description: '开放供给方提交报价方案',
        dotBg: 'bg-primary-500',
        icon: 'ri-auction-line',
      };
    case 'BID_DEADLINE': {
      const isPast = event.occurred_at ? new Date(event.occurred_at).getTime() <= Date.now() : false;
      return {
        title: '招募截止时间',
        description: isPast ? '已到达截止时间 (招募结束)' : '预设截止时间 (招募进行中)',
        dotBg: isPast ? 'bg-slate-400' : 'bg-amber-500',
        icon: isPast ? 'ri-timer-flash-line' : 'ri-timer-line',
        isCurrent: false,
      };
    }
    case 'BID_CREATED':
      return {
        title: '提交竞标',
        description: '供给方提交了新的报价方案',
        dotBg: 'bg-blue-500',
        icon: 'ri-price-tag-3-line',
      };
    case 'BID_WITHDRAWN':
      return {
        title: '撤回竞标',
        description: '供给方撤回了该竞标报价',
        dotBg: 'bg-gray-400',
        icon: 'ri-reply-line',
      };
    case 'BID_REJECTED':
      return {
        title: '拒绝竞标',
        description: '竞标报价未被需求企业采纳',
        dotBg: 'bg-rose-400',
        icon: 'ri-close-circle-line',
      };
    case 'BID_AWARDED':
    case 'AWARDED':
      return {
        title: '确定中标者',
        description: event.intent_id ? `已采纳中标方案 (竞标ID: ${event.intent_id})` : '企业确定并采纳中标方案',
        dotBg: 'bg-amber-400',
        icon: 'ri-trophy-line',
        isCurrent: false,
      };
    case 'CURRENT_TIME':
      return {
        title: '当前时间',
        description: '',
        dotBg: 'bg-indigo-600',
        icon: 'ri-time-fill',
        isCurrent: true,
      };
    default:
      return {
        title: event.event_type || '动态变更',
        description: event.status_to ? `状态变更: ${event.status_to}` : '',
        dotBg: 'bg-primary-500',
        icon: 'ri-time-line',
        isCurrent: false,
      };
  }
}

/** 至少包含发布、开启、截止、中标以及当前时间事件并按 occurred_at ASC, id ASC 稳定排序 */
function getMergedAndSortedTimeline(
  demand: Api.Demand.DemandResponse | null,
  rawTimeline: Api.Demand.DemandBidEventResponse[]
): Api.Demand.DemandBidEventResponse[] {
  const events = [...rawTimeline];
  const hasEventType = (type: string) =>
    events.some((e) => (e.event_type || '').toUpperCase() === type);

  // 补全需求发布时间节点
  if (demand?.published_at && !hasEventType('DEMAND_PUBLISHED')) {
    events.push({
      id: `synthetic_pub_${demand.id}`,
      demand_id: demand.id,
      event_type: 'DEMAND_PUBLISHED',
      occurred_at: demand.published_at,
    });
  }

  // 补全竞标开启时间节点
  if (demand?.published_at && !hasEventType('BID_OPENED')) {
    events.push({
      id: `synthetic_open_${demand.id}`,
      demand_id: demand.id,
      event_type: 'BID_OPENED',
      occurred_at: demand.published_at,
    });
  }

  // 补全招募截止时间节点
  if (demand?.bid_deadline_at && !hasEventType('BID_DEADLINE')) {
    events.push({
      id: `synthetic_deadline_${demand.id}`,
      demand_id: demand.id,
      event_type: 'BID_DEADLINE',
      occurred_at: demand.bid_deadline_at,
    });
  }

  // 插入当前时间节点
  if (!hasEventType('CURRENT_TIME')) {
    events.push({
      id: `synthetic_now_${demand?.id || 'now'}`,
      demand_id: demand?.id,
      event_type: 'CURRENT_TIME',
      occurred_at: new Date().toISOString(),
    });
  }

  // 按 occurred_at ASC, id ASC 稳定排序
  return events.sort((a, b) => {
    const timeA = a.occurred_at ? new Date(a.occurred_at).getTime() : 0;
    const timeB = b.occurred_at ? new Date(b.occurred_at).getTime() : 0;
    if (timeA !== timeB) {
      return timeA - timeB;
    }
    const idA = a.id || '';
    const idB = b.id || '';
    return idA.localeCompare(idB);
  });
}

export default function MyDemandDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [demand, setDemand] = useState<Api.Demand.DemandResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 倒计时计时器，每秒刷新一次
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!demand?.bid_deadline_at) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [demand?.bid_deadline_at]);

  const countdownInfo = formatCountdown(demand?.bid_deadline_at);

  // 报价列表与时间线
  const [bids, setBids] = useState<Api.Demand.BidResponse[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidsError, setBidsError] = useState('');

  const [timeline, setTimeline] = useState<Api.Demand.DemandBidEventResponse[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Toast 消息提示
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  }, []);

  // 按钮交互 Loading
  const [submittingAudit, setSubmittingAudit] = useState(false);
  const [closingDemand, setClosingDemand] = useState(false);
  const [awardingBidId, setAwardingBidId] = useState<string | null>(null);

  // 供给方详情/联系方式弹窗 state
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [providerDetail, setProviderDetail] = useState<Api.Provider.ProviderPublicProfileResponse | null>(null);
  const [providerContact, setProviderContact] = useState<Api.Provider.ProviderContactResponse | null>(null);
  const [loadingProviderDetail, setLoadingProviderDetail] = useState(false);
  const [providerDetailError, setProviderDetailError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleOpenProviderDetail = async (providerProfileId?: string) => {
    if (!providerProfileId) {
      showToast('该竞标服务商暂无公开档案', 'warning');
      return;
    }
    setSelectedProviderId(providerProfileId);
    setLoadingProviderDetail(true);
    setProviderDetailError(null);
    setProviderDetail(null);
    setProviderContact(null);

    try {
      const [detailRes, contactRes] = await Promise.allSettled([
        fetchGetPublicProviderDetail(providerProfileId),
        fetchViewProviderContact(providerProfileId)
      ]);

      if (detailRes.status === 'fulfilled') {
        setProviderDetail(detailRes.value);
      }
      if (contactRes.status === 'fulfilled') {
        setProviderContact(contactRes.value);
      }
      if (detailRes.status === 'rejected' && contactRes.status === 'rejected') {
        setProviderDetailError('获取服务商详情失败');
      }
    } catch (err) {
      console.error('获取服务商详情失败:', err);
      setProviderDetailError('获取服务商详情失败');
    } finally {
      setLoadingProviderDetail(false);
    }
  };

  const handleCopyText = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // 确认关闭弹窗
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();
  const categoriesFetchedRef = useRef(false);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (categoriesFetchedRef.current || categories.length > 0) return;
    categoriesFetchedRef.current = true;
    fetchCategories().catch((err) => {
      console.error('获取分类失败:', err);
      categoriesFetchedRef.current = false;
    });
  }, [categories.length, fetchCategories]);

  // 加载数据
  const loadDemandDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMyDemandDetail(id);
      setDemand(res || null);
    } catch (err: any) {
      console.error('获取需求详情失败:', err);
      setError(err?.message || '获取需求详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 加载报价列表
  const loadBids = useCallback(async () => {
    if (!id) return;
    setBidsLoading(true);
    setBidsError('');
    try {
      const res = await fetchEnterpriseDemandBids(id);
      setBids(res?.items || []);
    } catch (err: any) {
      console.error('获取报价列表失败:', err);
      setBidsError('暂无法加载该需求的报价列表');
    } finally {
      setBidsLoading(false);
    }
  }, [id]);

  // 加载时间线
  const loadTimeline = useCallback(async () => {
    if (!id) return;
    setTimelineLoading(true);
    try {
      const res = await fetchEnterpriseDemandBidTimeline(id);
      setTimeline(res || []);
    } catch (err) {
      console.error('获取报价时间线失败:', err);
    } finally {
      setTimelineLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    loadDemandDetail();
    loadBids();
    loadTimeline();
  }, [id, loadDemandDetail, loadBids, loadTimeline]);

  // 提交审核
  const handleSubmitAudit = async () => {
    if (!id || submittingAudit) return;
    setSubmittingAudit(true);
    try {
      await submitDemandAudit(id);
      showToast('已提交审核', 'success');
      await loadDemandDetail();
    } catch (err: any) {
      showToast(err?.message || '提交审核失败，请稍后重试', 'error');
    } finally {
      setSubmittingAudit(false);
    }
  };

  // 确认关闭需求
  const handleConfirmClose = async () => {
    if (!id || closingDemand) return;
    setClosingDemand(true);
    try {
      await closeDemand(id, { reason: closeReason.trim() });
      setShowCloseModal(false);
      showToast('需求已关闭', 'success');
      await loadDemandDetail();
    } catch (err: any) {
      showToast(err?.message || '关闭需求失败，请稍后重试', 'error');
    } finally {
      setClosingDemand(false);
    }
  };

  // 采纳报价
  const handleAwardBid = async (bidId?: string) => {
    if (!id || !bidId || awardingBidId) return;
    if (!window.confirm('确认采纳该报价？采纳后需求将被标记为已达成方案。')) return;
    setAwardingBidId(bidId);
    try {
      await awardDemandBid(id, bidId);
      showToast('已成功采纳报价！', 'success');
      await loadDemandDetail();
      await loadBids();
    } catch (err: any) {
      showToast(err?.message || '采纳报价失败，请稍后重试', 'error');
    } finally {
      setAwardingBidId(null);
    }
  };

  const statusInfo = getStatusBadge(demand?.status);

  const categoryNames =
    demand?.category_codes && demand.category_codes.length > 0
      ? demand.category_codes.map((code) => getCategoryNameByCode(code, code))
      : [];

  const requirements =
    demand?.undertaking_requirements && demand.undertaking_requirements.length > 0
      ? demand.undertaking_requirements
      : demand?.undertaking_requirements_summary && demand.undertaking_requirements_summary.length > 0
        ? demand.undertaking_requirements_summary
        : [];

  return (
    <AccountLayout
      activePath="/my-postings"
      breadcrumbs={[
        { label: '账号中心', href: '/profile' },
        { label: '需求发布', href: '/my-postings' },
        { label: '需求详情' },
      ]}
    >
      {/* Back Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <button
          onClick={() => navigate('/my-postings')}
          className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm"></i>
          返回需求发布记录
        </button>
        {demand?.id && (
          <span className="text-xs text-foreground-400 font-mono">
            需求编号: {demand.id}
          </span>
        )}
      </div>

      {loading ? (
        <div className="glass-card rounded-2xl p-16 text-center border border-background-200">
          <div className="inline-block w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-foreground-400 mt-3">正在加载需求详情...</p>
        </div>
      ) : error || !demand ? (
        <div className="glass-card rounded-2xl p-12 text-center border border-red-200 bg-red-50/50">
          <i className="ri-error-warning-line text-3xl text-rose-500 mb-2 block"></i>
          <p className="text-sm font-semibold text-rose-700 mb-4">{error || '未能查找到该需求'}</p>
          <button
            onClick={() => navigate('/my-postings')}
            className="px-4 py-2 bg-rose-600 text-white text-xs font-medium rounded-xl hover:bg-rose-500 transition-colors cursor-pointer"
          >
            返回需求列表
          </button>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* ===== Left Column: Demand Main Info, Description, Requirements, Attachments, Bids ===== */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Main Card */}
            <div className="glass-card rounded-2xl p-6 border border-background-200">
              {/* Top row: Badges and Status */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-primary-100 text-primary-700">
                    我的需求
                  </span>
                  {categoryNames.map((cat, idx) => (
                    <span key={idx} className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-purple-50 text-purple-700 border border-purple-100">
                      {cat}
                    </span>
                  ))}
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${statusInfo.bg}`}>
                  {statusInfo.label}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-xl font-bold text-foreground-950 mb-4 leading-snug">
                {demand.title}
              </h1>

              {/* Meta statistics */}
              <div className="p-4 rounded-xl bg-background-50 border border-background-200/80 mb-6 space-y-4">
                <div className="pb-3 border-b border-background-200/60 flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground-600">需求预算</span>
                  <span className="text-xl font-bold text-red-500 font-heading">
                    {formatDemandBudget(demand)}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <span className="text-[11px] text-foreground-400 block mb-0.5">招募截止时间</span>
                    <span className="text-xs font-semibold text-foreground-800">
                      {formatDateShort(demand.bid_deadline_at) || '未设定'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-foreground-400 block mb-0.5">预计交付时间</span>
                    <span className="text-xs font-semibold text-foreground-800">
                      {formatDateShort(demand.expected_delivery_at) || '未设定'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-foreground-400 block mb-0.5">发布时间</span>
                    <span className="text-xs font-semibold text-foreground-800">
                      {formatDateTime(demand.published_at) || '草稿阶段'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons Header */}
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-background-200/80">
                {statusInfo.type === 'draft' && (
                  <>
                    <Link
                      to={`/post-demand?id=${demand.id}`}
                      className="px-4 py-2 rounded-xl bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-all cursor-pointer"
                    >
                      继续编辑
                    </Link>
                    <button
                      onClick={handleSubmitAudit}
                      disabled={submittingAudit}
                      className="px-4 py-2 rounded-xl border border-primary-300 text-xs font-semibold text-primary-700 hover:bg-primary-50 transition-all cursor-pointer disabled:opacity-50"
                    >
                      {submittingAudit ? '提交中...' : '提交审核'}
                    </button>
                  </>
                )}

                {statusInfo.type === 'rejected' && (
                  <Link
                    to={`/post-demand?id=${demand.id}`}
                    className="px-4 py-2 rounded-xl bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-all cursor-pointer"
                  >
                    修改并重新提交
                  </Link>
                )}

                {statusInfo.type === 'published' && (
                  <button
                    onClick={() => setShowCloseModal(true)}
                    className="px-4 py-2 rounded-xl border border-rose-200 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer"
                  >
                    关闭需求
                  </button>
                )}

                <Link
                  to={`/demands/${demand.id}`}
                  target="_blank"
                  className="px-4 py-2 rounded-xl bg-background-100 hover:bg-background-200 text-xs font-medium text-foreground-700 transition-all cursor-pointer ml-auto"
                >
                  预览前台公开页 <i className="ri-external-link-line ml-1"></i>
                </Link>
              </div>
            </div>

            {/* Description Card */}
            <div className="glass-card rounded-2xl p-6 border border-background-200">
              <h2 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-file-text-line text-primary-600"></i>
                需求详细描述
              </h2>
              <p className="text-xs text-foreground-700 leading-relaxed whitespace-pre-line bg-background-50 p-4 rounded-xl border border-background-200/60">
                {demand.description || demand.description_summary || '暂无描述内容'}
              </p>
            </div>

            {/* Requirements Card */}
            {requirements.length > 0 && (
              <div className="glass-card rounded-2xl p-6 border border-background-200">
                <h2 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-amber-600"></i>
                  承接要求
                </h2>
                <ul className="space-y-2">
                  {requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs text-foreground-700 p-2.5 rounded-xl bg-background-50 border border-background-200/50">
                      <span className="shrink-0 w-5 h-5 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                      </span>
                      <span className="pt-0.5">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Attachments Card */}
            {demand.attachments && demand.attachments.length > 0 && (
              <div className="glass-card rounded-2xl p-6 border border-background-200">
                <h2 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-attachment-line text-blue-600"></i>
                  需求附件 ({demand.attachments.length})
                </h2>
                <div className="space-y-2">
                  {demand.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200/60 text-xs">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <i className="ri-file-line text-base text-foreground-400"></i>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground-900 truncate">
                            {att.display_name || att.file_name || '附件'}
                          </p>
                          {att.file_size && (
                            <p className="text-[10px] text-foreground-400">{formatFileSize(att.file_size)}</p>
                          )}
                        </div>
                      </div>
                      {att.access_url && (
                        <a
                          href={att.access_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-primary-50 text-primary-700 font-medium hover:bg-primary-100 transition-colors cursor-pointer shrink-0"
                        >
                          下载附件
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bids Section */}
            <div className="glass-card rounded-2xl p-6 border border-background-200">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-emerald-600"></i>
                  竞标报价列表
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                    共 {bids.length} 人竞标
                  </span>
                </h2>
              </div>

              {bidsLoading ? (
                <div className="py-8 text-center text-xs text-foreground-400">
                  <div className="inline-block w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                  <p>正在加载报价列表...</p>
                </div>
              ) : bidsError ? (
                <p className="text-xs text-rose-500 py-4 text-center">{bidsError}</p>
              ) : bids.length === 0 ? (
                <div className="py-8 text-center bg-background-50 rounded-xl border border-background-200/60">
                  <i className="ri-inbox-line text-2xl text-foreground-300 mb-1 block"></i>
                  <p className="text-xs text-foreground-500">暂无服务商对该需求提交报价</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...bids]
                    .sort((a, b) => {
                      const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
                      const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
                      return tB - tA; // 竞标时间倒序
                    })
                    .map((bid) => {
                      const bidStatusInfo = getBidStatusBadge(bid.bid_status);
                      const displayName = bid.enterprise_name_masked || '供给方';
                      const currencyText = bid.currency ? ` (${bid.currency})` : '';

                      return (
                        <div
                          key={bid.id}
                          className="p-4 rounded-xl bg-background-50 border border-background-200/70 space-y-3 hover:border-primary-200 transition-all"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold border ${bidStatusInfo.color}`}>
                                {bidStatusInfo.label}
                              </span>
                              <span className="text-xs font-bold text-foreground-900">
                                {displayName}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs text-foreground-400">报价:</span>
                              <span className="text-base font-bold text-primary-700">
                                {formatCurrencyPrice(bid.quoted_amount, bid.currency)}
                              </span>
                              {bid.expected_delivery_days !== undefined && (
                                <span className="text-xs font-medium text-foreground-600 ml-2 bg-white px-2 py-0.5 rounded border border-background-200">
                                  预计交付: <strong className="text-foreground-900">{bid.expected_delivery_days}</strong> 天
                                </span>
                              )}
                            </div>
                          </div>

                          {bid.message && (
                            <p className="text-xs text-foreground-600 bg-white p-3 rounded-lg border border-background-200/60 leading-relaxed">
                              <span className="font-semibold text-foreground-800 mr-1">竞标说明:</span>
                              {bid.message}
                            </p>
                          )}

                          <div className="flex items-center justify-between gap-2 text-[11px] text-foreground-400 pt-1 border-t border-background-200/50">
                            <span>竞标提交时间: {formatDateTime(bid.created_at)}</span>
                            <div className="flex items-center gap-2">
                              {bid.provider_profile_id && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenProviderDetail(bid.provider_profile_id)}
                                  className="px-3 py-1 rounded-lg border border-primary-300/80 bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium text-xs transition-colors cursor-pointer inline-flex items-center gap-1"
                                >
                                  <i className="ri-user-search-line"></i>
                                  查看详情
                                </button>
                              )}
                              {statusInfo.type === 'published' &&
                                (bid.bid_status || '').toUpperCase() !== 'SELECTED' &&
                                (bid.bid_status || '').toUpperCase() !== 'AWARDED' && (
                                  <button
                                    onClick={() => handleAwardBid(bid.id)}
                                    disabled={awardingBidId === bid.id}
                                    className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-xs transition-colors cursor-pointer disabled:opacity-50"
                                  >
                                    {awardingBidId === bid.id ? '处理中...' : '采纳此报价'}
                                  </button>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {/* ===== Right Column: Sidebar (Timeline & Quick Summary) ===== */}
          <div className="w-full lg:w-[360px] shrink-0 space-y-6 lg:sticky lg:top-28">
            {/* Timeline Section */}
            {(() => {
              const sortedTimeline = getMergedAndSortedTimeline(demand, timeline);
              if (sortedTimeline.length === 0) return null;

              return (
                <div className="glass-card rounded-2xl p-6 border border-background-200">
                  <div className="flex items-center justify-between gap-4 mb-5 pb-3 border-b border-background-200/60">
                    <h2 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                      <i className="ri-history-line text-purple-600"></i>
                      需求报价时间线
                    </h2>
                  </div>
                  {timelineLoading ? (
                    <p className="text-xs text-foreground-400 py-2">加载时间线中...</p>
                  ) : (
                    <div className="relative pl-7 space-y-4 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-background-200">
                      {sortedTimeline.map((event, idx) => {
                        const details = getTimelineEventDetails(event);
                        const isCurrentTimeEvent = (event.event_type || '').toUpperCase() === 'CURRENT_TIME';

                        return (
                          <div
                            key={event.id || idx}
                            className={`relative text-xs p-3 rounded-xl transition-all ${details.isCurrent
                              ? 'bg-indigo-50/90 border border-indigo-200/90 shadow-xs'
                              : 'bg-background-50/60 border border-background-200/50'
                              }`}
                          >
                            <div className={`absolute -left-[30px] top-3.5 w-3.5 h-3.5 rounded-full ${details.dotBg} ring-4 ring-white`}></div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <i className={`${details.icon} text-sm ${details.isCurrent ? 'text-indigo-600' : 'text-foreground-600'}`}></i>
                                <span className={`font-bold ${details.isCurrent ? 'text-indigo-950 text-sm' : 'text-foreground-900'}`}>
                                  {details.title}
                                </span>
                                {details.description && (
                                  <span className={`text-xs font-normal truncate ${details.isCurrent ? 'text-indigo-700 font-medium' : 'text-foreground-500'}`}>
                                    - {details.description}
                                  </span>
                                )}
                              </div>
                              {event.occurred_at && (
                                <span
                                  className={`text-xs font-mono font-semibold px-2.5 py-0.5 rounded-md shrink-0 ml-auto ${details.isCurrent
                                    ? 'bg-indigo-600 text-white shadow-xs'
                                    : 'text-foreground-500 bg-white border border-background-200'
                                    }`}
                                >
                                  {formatDateTime(event.occurred_at)}
                                </span>
                              )}
                            </div>

                            {/* 倒计时单独占一行，展示在“当前时间”节点下 */}
                            {isCurrentTimeEvent && demand?.bid_deadline_at && (
                              <div className="mt-2.5 pt-2 border-t border-indigo-100 flex items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-1.5 font-medium text-indigo-900">
                                  <i className={`ri-time-line ${!countdownInfo.isExpired ? 'text-amber-600 animate-pulse' : 'text-foreground-400'}`}></i>
                                  <span>招募截止：</span>
                                </div>
                                <span
                                  className={`font-mono font-bold px-2 py-0.5 rounded-md ${countdownInfo.isExpired
                                    ? 'bg-gray-100 text-gray-500'
                                    : 'bg-amber-100/80 text-amber-900 border border-amber-200/80'
                                    }`}
                                >
                                  {countdownInfo.text}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Quick Summary Card */}
            <div className="glass-card rounded-2xl p-6 border border-background-200 space-y-4">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-dashboard-3-line text-secondary-600"></i>
                需求概览面板
              </h3>
              <div className="space-y-3 text-xs text-foreground-700">
                <div className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200/60">
                  <span className="text-foreground-500">招募状态</span>
                  <span className={`px-2.5 py-0.5 rounded-full font-semibold border ${statusInfo.bg}`}>
                    {statusInfo.label}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200/60">
                  <span className="text-foreground-500">已有竞标报价</span>
                  <span className="font-bold text-foreground-900 text-sm">
                    {bids.length} 人提交
                  </span>
                </div>
                {demand.enterprise_name_masked && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200/60">
                    <span className="text-foreground-500">需求主体</span>
                    <span className="font-semibold text-foreground-900">
                      {demand.enterprise_name_masked}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Demand Confirmation Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 animate-[fadeIn_0.15s_ease-out]">
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto text-xl">
              <i className="ri-error-warning-line"></i>
            </div>
            <div className="text-center">
              <h3 className="text-base font-bold text-foreground-900">确认关闭此需求？</h3>
              <p className="text-xs text-foreground-500 mt-1.5">
                关闭后该需求将停止招募，不再接收新的竞标报价。
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1">
                关闭原因（选填）
              </label>
              <input
                type="text"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="例如：已有合适承接方 / 项目调整"
                className="w-full px-3 py-2 rounded-xl border border-background-300 text-xs outline-none focus:border-primary-400"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                disabled={closingDemand}
                onClick={() => setShowCloseModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-background-300 text-foreground-600 text-xs font-semibold hover:bg-background-100 cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                disabled={closingDemand}
                onClick={handleConfirmClose}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-xs font-semibold hover:bg-rose-500 disabled:opacity-50 cursor-pointer"
              >
                {closingDemand ? '关闭中...' : '确认关闭'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Detail & Contact Modal */}
      {selectedProviderId && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5 animate-[fadeIn_0.15s_ease-out] relative max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedProviderId(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Title */}
            <div className="flex items-center gap-2 pb-3 border-b border-background-200">
              <i className="ri-user-star-line text-xl text-emerald-600"></i>
              <h3 className="text-base font-bold text-foreground-900">竞标服务商详情</h3>
            </div>

            {loadingProviderDetail ? (
              <div className="py-12 text-center text-xs text-foreground-400">
                <div className="inline-block w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <p>正在获取服务商资料与联系方式...</p>
              </div>
            ) : providerDetailError && !providerDetail && !providerContact ? (
              <div className="py-8 text-center space-y-2">
                <p className="text-xs text-rose-500">{providerDetailError}</p>
                <button
                  type="button"
                  onClick={() => setSelectedProviderId(null)}
                  className="px-4 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs cursor-pointer"
                >
                  关闭
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* 基本架构信息 */}
                {providerDetail && (
                  <div className="p-4 rounded-xl bg-background-50 border border-background-200/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-sm text-foreground-900 flex items-center gap-2">
                        {providerDetail.display_name || providerDetail.enterprise_name_masked || '服务商'}
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800">
                          {providerDetail.provider_type === 'ENTERPRISE' ? '企业团队' : '个人创作者'}
                        </span>
                      </div>
                    </div>

                    {providerDetail.intro && (
                      <p className="text-xs text-foreground-600 leading-relaxed bg-white p-2.5 rounded-lg border border-background-200/60 line-clamp-3">
                        {providerDetail.intro}
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-xs text-foreground-600 pt-1">
                      {providerDetail.city_name && (
                        <div>
                          <span className="text-foreground-400">常驻城市：</span>
                          <span className="font-medium text-foreground-800">{providerDetail.city_name}</span>
                        </div>
                      )}
                      {providerDetail.daily_rate !== undefined && (
                        <div>
                          <span className="text-foreground-400">预算/日薪：</span>
                          <span className="font-bold text-emerald-700">{providerDetail.daily_rate} {providerDetail.currency || 'CNY'}</span>
                        </div>
                      )}
                    </div>

                    {providerDetail.skill_tags && providerDetail.skill_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {providerDetail.skill_tags.map((tag, idx) => (
                          <span key={idx} className="text-[10px] px-2 py-0.5 rounded bg-white text-gray-600 border border-gray-200">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 联系方式区域 (fetchViewProviderContact 接口数据) */}
                <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-200/80 space-y-3">
                  <h4 className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                    <i className="ri-contacts-book-line text-emerald-600 text-sm"></i>
                    服务商联系方式
                  </h4>

                  <div className="space-y-2 text-xs">
                    {/* 电话 */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-emerald-100">
                      <div className="flex items-center gap-2 text-foreground-700">
                        <i className="ri-phone-line text-emerald-600"></i>
                        <span>联系电话：</span>
                        <strong className="font-mono text-foreground-900">
                          {providerContact?.phone || providerDetail?.contact_phone_masked || '暂无'}
                        </strong>
                      </div>
                      {Boolean(providerContact?.phone) && (
                        <button
                          type="button"
                          onClick={() => handleCopyText(providerContact?.phone || '', 'phone')}
                          className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 cursor-pointer"
                        >
                          {copiedField === 'phone' ? '已复制' : '复制'}
                        </button>
                      )}
                    </div>

                    {/* 微信 */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-emerald-100">
                      <div className="flex items-center gap-2 text-foreground-700">
                        <i className="ri-wechat-line text-emerald-600"></i>
                        <span>微信号：</span>
                        <strong className="font-mono text-foreground-900">
                          {providerContact?.wechat_id || providerDetail?.contact_wechat_id_masked || '暂无'}
                        </strong>
                      </div>
                      {Boolean(providerContact?.wechat_id) && (
                        <button
                          type="button"
                          onClick={() => handleCopyText(providerContact?.wechat_id || '', 'wechat')}
                          className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 cursor-pointer"
                        >
                          {copiedField === 'wechat' ? '已复制' : '复制'}
                        </button>
                      )}
                    </div>

                    {/* 邮箱 */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-emerald-100">
                      <div className="flex items-center gap-2 text-foreground-700 truncate pr-2">
                        <i className="ri-mail-line text-emerald-600 shrink-0"></i>
                        <span className="shrink-0">电子邮箱：</span>
                        <strong className="font-mono text-foreground-900 truncate">
                          {providerContact?.email || providerDetail?.contact_email_masked || '暂无'}
                        </strong>
                      </div>
                      {Boolean(providerContact?.email) && (
                        <button
                          type="button"
                          onClick={() => handleCopyText(providerContact?.email || '', 'email')}
                          className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 shrink-0 cursor-pointer"
                        >
                          {copiedField === 'email' ? '已复制' : '复制'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-between gap-3 pt-2">
                  <Link
                    to={`/talents/${selectedProviderId}`}
                    target="_blank"
                    className="flex-1 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold text-center transition-colors cursor-pointer inline-flex items-center justify-center gap-1"
                  >
                    <span>查看人才公开主页</span>
                    <i className="ri-external-link-line"></i>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setSelectedProviderId(null)}
                    className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-semibold transition-colors cursor-pointer"
                  >
                    关闭
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Toast 提示 */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AccountLayout>
  );
}
